import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, Circle, X, Sparkles, ArrowRight, Lightbulb } from "lucide-react";

const STEPS = [
  { key: "profile", label: "Complete your profile", description: "First name, last name, and phone in Settings", to: "/settings" },
  { key: "buyer_profile", label: "Set up your buyer profile", description: "Signer info for auto-filled contracts (LLC optional)", to: "/profile" },
  { key: "kpi_goals", label: "Set your weekly KPI goals", description: "Calls, texts, leads, offers. Your weekly targets.", to: "/kpis" },
  { key: "first_lead", label: "Add your first lead", description: "Drop in whoever you're chasing right now", to: "/leads" },
];

export function OnboardingChecklist() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem("wu_onboarding_hidden") === "1");

  const load = async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
    } catch {
      // silent
    }
  };

  // eslint-disable-next-line
  useEffect(() => { if (user && !user.is_admin) load(); }, [user]);

  if (!user || user.is_admin) return null;
  if (!status || status.all_done || hidden) return null;
  const doneCount = STEPS.filter((s) => status[s.key]).length;
  const total = STEPS.length;
  const pct = Math.round((doneCount / total) * 100);

  return (
    <div className="surface-card p-5 mb-10" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={16} style={{ color: "var(--lime)" }} />
          <h3 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
            Get set up · {doneCount}/{total}
          </h3>
        </div>
        <button
          onClick={() => { localStorage.setItem("wu_onboarding_hidden", "1"); setHidden(true); }}
          className="p-1 transition hover:opacity-70"
          data-testid="onboarding-dismiss"
          title="Hide for now"
        >
          <X size={14} style={{ color: "var(--text-muted)" }} />
        </button>
      </div>
      <div className="w-full h-1.5 rounded-full mb-4" style={{ background: "var(--surface2)" }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "var(--lime)" }} />
      </div>
      <div className="space-y-2">
        {STEPS.map((s) => {
          const done = status[s.key];
          const Inner = (
            <>
              <div className="shrink-0 mt-0.5">
                {done ? (
                  <div className="flex items-center justify-center rounded-full" style={{ width: 20, height: 20, background: "var(--lime)", color: "#0c0c0c" }}>
                    <Check size={12} strokeWidth={3} />
                  </div>
                ) : (
                  <Circle size={20} style={{ color: "var(--text-muted)" }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm" style={{ color: done ? "var(--text-muted)" : "var(--text)", textDecoration: done ? "line-through" : "none" }}>{s.label}</div>
                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{s.description}</div>
              </div>
              {!done && <ArrowRight size={14} className="shrink-0" style={{ color: "var(--text-muted)" }} />}
            </>
          );
          return (
            <Link
              key={s.key}
              to={s.to}
              className="flex items-start gap-3 p-3 rounded-2xl transition hover:opacity-90"
              style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
              data-testid={`onboarding-step-${s.key}`}
            >
              {Inner}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ---- Welcome modal (Layer 1) ----

export function WelcomeModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || user.is_admin) return;
    if (localStorage.getItem("wu_welcome_seen")) return;
    // Only ever on a first login: the server flag survives new devices.
    api.get("/onboarding/flags")
      .then(({ data }) => {
        if (data?.welcome_seen) {
          localStorage.setItem("wu_welcome_seen", "1");
        } else {
          setOpen(true);
        }
      })
      .catch(() => setOpen(true));
  }, [user]);

  const close = async () => {
    localStorage.setItem("wu_welcome_seen", "1");
    setOpen(false);
    try { await api.post("/onboarding/flag", { flag: "welcome_seen", value: true }); } catch { /* silent */ }
  };

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || !user || user.is_admin) return null;
  const firstName = user.first_name || (user.name || "").split(" ")[0] || "there";

  return createPortal((
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
      onClick={close}
      data-testid="welcome-modal"
    >
      <div
        className="w-full max-w-lg rounded-2xl text-center relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 40 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-4 right-4 p-1.5 rounded-xl transition hover:bg-white/10"
          style={{ color: "var(--text-muted)" }}
          aria-label="Close"
          data-testid="welcome-modal-close"
        >
          <X size={16} />
        </button>
        <div className="inline-flex items-center justify-center rounded-2xl mb-5" style={{ width: 64, height: 64, background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--border-lime)" }}>
          <Sparkles size={32} />
        </div>
        <h2 className="h-display text-3xl mb-3">Welcome, {firstName} 👋</h2>
        <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
          This is your wholesaling command center. CRM, contracts, calculator, calendar, AI coach, all here so you can spend less time on busywork and more time on the phone closing deals.
          <br /><br />
          Let&apos;s get you set up in about 5 minutes.
        </p>
        <button
          onClick={close}
          className="btn-primary w-full text-base py-3 flex items-center justify-center gap-2"
          data-testid="welcome-modal-cta"
        >
          Let&apos;s go <ArrowRight size={14} />
        </button>
      </div>
    </div>
  ), document.body);
}

// ---- Page tooltip (Layer 3) ----

export function PageTip({ flag, title, children }) {
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(`wu_tip_${flag}`));
  if (dismissed) return null;
  const close = async () => {
    localStorage.setItem(`wu_tip_${flag}`, "1");
    setDismissed(true);
    try { await api.post("/onboarding/flag", { flag: `tour_${flag}_seen`, value: true }); } catch { /* silent */ }
  };
  return (
    <div
      className="flex items-start gap-3 p-4 rounded-xl mb-4"
      style={{ background: "var(--lime-glow)", border: "1px solid var(--border-lime)" }}
      data-testid={`page-tip-${flag}`}
    >
      <Lightbulb size={18} style={{ color: "var(--lime)" }} className="mt-0.5 shrink-0" />
      <div className="flex-1 text-sm" style={{ color: "var(--text)" }}>
        {title && <div className="font-semibold mb-1" style={{ color: "var(--lime)" }}>{title}</div>}
        {children}
      </div>
      <button onClick={close} className="p-1 transition hover:opacity-70 shrink-0" data-testid={`page-tip-dismiss-${flag}`}>
        <X size={14} style={{ color: "var(--text-muted)" }} />
      </button>
    </div>
  );
}
